import numeral from 'numeral'
import {
  DynamoFile,
  FileCommentTopic,
  FileStatus,
  PopupTextType,
} from '../../types'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import { fileContainsComment, fileSizeLessThan5MB } from '../../utils/common'
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined'
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import FileUnorderedListOptionBtn from './FileUnorderedListOptionBtn'
import { VirtualItem } from '@tanstack/react-virtual'
import {
  DELETE_FILE,
  isUnhealthyFile,
  renderIndicator,
  renderUnhealthyFileBadge,
} from '../../utils/file_management_utils'

type Props = {
  file: DynamoFile
  virtualItem: VirtualItem
  onFileClick: (file: DynamoFile) => () => void
  selectMultipleFiles: boolean
  addFileIdToSelectedFileRef: (fileId: string) => void
  fileHasChecked: (fileId: string) => boolean
  comments: FileCommentTopic[]
  showPopup: (popup: PopupTextType) => void
  deletePDF: (fileId: string) => Promise<void>
  showRenamePopup: (fileId: string) => void
  movable: boolean
  move?: ({
    id,
    name,
    type,
  }: {
    id: string
    name: string
    type: 'folder' | 'file'
  }) => void
  savePdfsToWorkbook: (file: DynamoFile) => Promise<void>
  isTrimmable: (fileId: string) => any
  trimFile: (fileId: string) => void
  isExportable: (fileId: string) => boolean
  exportPDF: (file: DynamoFile) => () => Promise<void>
  downloadFile: (file: DynamoFile) => () => Promise<void>
  lastItem: boolean
  firstItem: boolean
}

const FileItem = ({
  file,
  virtualItem,
  addFileIdToSelectedFileRef,
  fileHasChecked,
  onFileClick,
  selectMultipleFiles,
  comments,
  showPopup,
  deletePDF,
  movable,
  showRenamePopup,
  move,
  savePdfsToWorkbook,
  isTrimmable,
  trimFile,
  exportPDF,
  isExportable,
  downloadFile,
  firstItem,
  lastItem,
}: Props) => {
  return (
    <div
      key={file.fileId}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      <li className={`rounded-md p-1 hover:bg-gray-100`}>
        <div className="">
          <div className="flex justify-between items-center h-11">
            <div
              className="flex w-full h-full justify-start items-center cursor-pointer"
              onClick={onFileClick(file)}
            >
              {selectMultipleFiles && (
                <div className="flex items-center p-2">
                  <input
                    type="checkbox"
                    name="select-all"
                    className="mr-2 accent-[#107C41] border-[#616161] w-4 h-4"
                    onChange={() => addFileIdToSelectedFileRef(file.fileId)}
                    checked={fileHasChecked(file.fileId)}
                  />
                </div>
              )}
              {!selectMultipleFiles && (
                <div className="flex-shrink-0">
                  {renderIndicator(file.status)}
                </div>
              )}
              <div className="mr-2">
                <h3 className="text-sm font-medium leading-5">
                  {file.fileName.length > 30
                    ? file.fileName.slice(0, 27) + '...'
                    : file.fileName}
                </h3>
              </div>
              {!isUnhealthyFile(file.status) && (
                <div className="text-xs font-normal leading-4 text-gray-500 mr-2">
                  {numeral(file.originalFileSize).format('0b').toString()}
                </div>
              )}
              {!file.isLocal && (
                <div className="flex mr-2 items-center">
                  <CloudOutlinedIcon sx={{ fontSize: 12 }} />
                </div>
              )}
              {!isUnhealthyFile(file.status) &&
                fileContainsComment(file, comments) && (
                  <div className="flex mr-2 items-center">
                    <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 12 }} />
                  </div>
                )}
              {isUnhealthyFile(file.status) && (
                <div className="bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
                  {renderUnhealthyFileBadge(file.status)}
                </div>
              )}
            </div>
            {file.status !== FileStatus.SUCCEEDED &&
              file.status !== FileStatus.UPLOADING &&
              file.status !== FileStatus.PROCESSING &&
              !selectMultipleFiles && (
                <div className="relative group/singledeletefile flex justify-center rounded hover:bg-gray-500">
                  <button
                    className="w-full h-full p-2.5 rounded transition ease-in-out delay-100 hover:scale-110"
                    onClick={() =>
                      showPopup({
                        ...DELETE_FILE,
                        onClick: () => deletePDF(file.fileId),
                      })
                    }
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div
                    className={`absolute ${
                      lastItem
                        ? firstItem
                          ? 'bottom-8 before:-bottom-1'
                          : 'bottom-14 before:-bottom-1'
                        : 'top-14 before:-top-1'
                    }
                                  scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/singledeletefile:scale-100 group-hover/singledeletefile:w-fit before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-1  before:right-0 before:mx-auto`}
                  >
                    Delete
                  </div>
                </div>
              )}
            {file.status === FileStatus.SUCCEEDED && !selectMultipleFiles && (
              <div className="flex items-center group/ellipses">
                <FileUnorderedListOptionBtn
                  groupName="rename"
                  icon={
                    <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                  }
                  hidden={true}
                  tooltipPosition={`${
                    lastItem
                      ? firstItem
                        ? 'bottom-8 before:-bottom-1'
                        : 'bottom-14 before:-bottom-1'
                      : 'top-14 before:-top-1'
                  }`}
                  onClick={() => showRenamePopup(file.fileId)}
                />
                {movable && move && (
                  <div className="relative group/move hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                    <button
                      className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                      onClick={() =>
                        move({
                          id: file.fileId,
                          name: file.fileName,
                          type: 'file',
                        })
                      }
                    >
                      <DriveFileMoveOutlinedIcon sx={{ fontSize: 20 }} />
                    </button>
                    <div
                      className={`absolute ${
                        lastItem
                          ? firstItem
                            ? 'bottom-8 before:-bottom-1'
                            : 'bottom-14 before:-bottom-1'
                          : 'top-14 before:-top-1'
                      } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/move:scale-100 group-hover/move:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                    >
                      Move
                    </div>
                  </div>
                )}
                {!file.isLocal &&
                  fileSizeLessThan5MB(file.originalFileSize) && (
                    <div className="relative group/local hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                      <button
                        className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                        onClick={() => savePdfsToWorkbook(file)}
                      >
                        <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
                      </button>
                      <div
                        className={`absolute ${
                          lastItem
                            ? firstItem
                              ? 'bottom-8 before:-bottom-1'
                              : 'bottom-14 before:-bottom-1'
                            : 'top-14 before:-top-1'
                        } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/local:scale-100 group-hover/local:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                      >
                        Save to local
                      </div>
                    </div>
                  )}
                {isTrimmable(file.fileId) && (
                  <div className="relative group/trim hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                    <button
                      className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                      onClick={() => trimFile(file.fileId)}
                    >
                      <ContentCutOutlinedIcon sx={{ fontSize: 20 }} />
                    </button>
                    <div
                      className={`absolute ${
                        lastItem
                          ? firstItem
                            ? 'bottom-8 before:-bottom-1'
                            : 'bottom-14 before:-bottom-1'
                          : 'top-14 before:-top-1'
                      } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/trim:scale-100 group-hover/trim:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                    >
                      Delete pages without links
                    </div>
                  </div>
                )}
                {isExportable(file.fileId) && (
                  <div className="relative group/export hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                    <button
                      className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                      onClick={exportPDF(file)}
                    >
                      <SimCardDownloadOutlinedIcon sx={{ fontSize: 20 }} />
                    </button>
                    <div
                      className={`absolute ${
                        lastItem
                          ? firstItem
                            ? 'bottom-8 before:-bottom-1'
                            : 'bottom-14 before:-bottom-1'
                          : 'top-14 before:-top-1'
                      } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/export:scale-100 group-hover/export:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                    >
                      Export modified file
                    </div>
                  </div>
                )}
                <div className="relative group/download hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                  <button
                    className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                    onClick={downloadFile(file)}
                  >
                    <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div
                    className={`absolute ${
                      lastItem
                        ? firstItem
                          ? 'bottom-8 before:-bottom-1'
                          : 'bottom-14 before:-bottom-1'
                        : 'top-14 before:-top-1'
                    } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/download:scale-100 group-hover/download:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                  >
                    Export original file
                  </div>
                </div>
                <div className="relative group/deletefile hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                  <button
                    className="w-full h-full p-2.5 rounded transition ease-in-out delay-100 hover:scale-110"
                    onClick={() =>
                      showPopup({
                        ...DELETE_FILE,
                        onClick: () => deletePDF(file.fileId),
                      })
                    }
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div
                    className={`absolute ${
                      lastItem
                        ? firstItem
                          ? 'bottom-8 before:-bottom-1'
                          : 'bottom-14 before:-bottom-1'
                        : 'top-14 before:-top-1'
                    } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/deletefile:scale-100 group-hover/deletefile:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                  >
                    Delete
                  </div>
                </div>
                <div className="relative group/ellipses flex justify-center group-hover/ellipses:justify-center group-hover/ellipses:hidden mr-2 rounded hover:bg-gray-500">
                  <button className="w-full h-full p-2.5">
                    <MoreHorizOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </li>
    </div>
  )
}

export default FileItem
